import { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { 
  Plus, 
  Trash2, 
  Upload,
  Image,
  Calendar,
  MapPin,
  X,
  Camera,
  Video,
  Users,
  Activity
} from 'lucide-react'
import { useStore } from '../../store/useStore'
import GlassCard from '../../components/ui/GlassCard'
import Button from '../../components/ui/Button'
import AdminLayout from '../../components/admin/AdminLayout'

const GalleryManagement = () => {
  const { galleryImages, addGalleryImage, deleteGalleryImage } = useStore()
  const [activeCategory, setActiveCategory] = useState('activities')
  const [showForm, setShowForm] = useState(false)
  const [uploadMethod, setUploadMethod] = useState('url')
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const fileInputRef = useRef(null)

  const categories = [
    { id: 'activities', name: 'Activités', icon: Activity },
    { id: 'events', name: 'Événements', icon: Calendar },
    { id: 'residences', name: 'Résidences', icon: MapPin },
    { id: 'team', name: 'Équipe', icon: Users }
  ]

  const isVideoFile = (file) => {
    return file && file.type.startsWith('video/')
  }

  const isVideoUrl = (url) => {
    return url && (url.includes('.mp4') || url.includes('.webm') || url.includes('video/'))
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      const maxSize = 50 * 1024 * 1024 // 50MB
      if (file.size > maxSize) {
        alert('Fichier trop volumineux. Maximum: 50MB')
        return
      }
      
      setSelectedFile(file)
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const finalUrl = uploadMethod === 'file' ? previewUrl : imageUrl
    if (!finalUrl) {
      alert('Veuillez fournir une image ou vidéo')
      return
    }

    // Déterminer le type de média
    let mediaType = 'image'
    if (uploadMethod === 'file' && selectedFile) {
      mediaType = isVideoFile(selectedFile) ? 'video' : 'image'
    } else if (uploadMethod === 'url' && imageUrl) {
      mediaType = isVideoUrl(imageUrl) ? 'video' : 'image'
    }

    const imageData = {
      id: Date.now(),
      imageUrl: finalUrl,
      mediaType: mediaType,
      category: activeCategory
    }

    addGalleryImage(activeCategory, imageData)
    
    // Reset
    setShowForm(false)
    setSelectedFile(null)
    setPreviewUrl('')
    setImageUrl('')
    setUploadMethod('url')
  }

  const resetForm = () => {
    setShowForm(false)
    setSelectedFile(null)
    setPreviewUrl('')
    setImageUrl('')
    setUploadMethod('url')
  }

  const currentImages = galleryImages[activeCategory] || []

  return (
    <AdminLayout>
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion de la Galerie</h1>
              <p className="text-gray-600">Ajoutez des photos et vidéos par catégorie</p>
            </div>
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-primary-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </div>

          {/* Formulaire simple */}
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={resetForm}
            >
              <GlassCard className="p-6 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Ajouter un média</h2>
                  <button onClick={resetForm} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Sélection de catégorie */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Catégorie
                    </label>
                    <select
                      value={activeCategory}
                      onChange={(e) => setActiveCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {categories.map(cat => {
                        const Icon = cat.icon
                        return (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        )
                      })}
                    </select>
                  </div>

                  {/* Méthode d'upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Méthode d'upload
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="uploadMethod"
                          value="url"
                          checked={uploadMethod === 'url'}
                          onChange={(e) => setUploadMethod(e.target.value)}
                        />
                        <span>URL</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="uploadMethod"
                          value="file"
                          checked={uploadMethod === 'file'}
                          onChange={(e) => setUploadMethod(e.target.value)}
                        />
                        <span>Fichier</span>
                      </label>
                    </div>
                  </div>

                  {/* URL */}
                  {uploadMethod === 'url' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        URL de l'image ou vidéo
                      </label>
                      <input
                        type="url"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="https://example.com/image.jpg"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  ) : (
                    /* Fichier */
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sélectionner un fichier
                      </label>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        required
                      />
                      {previewUrl && (
                        <div className="mt-4">
                          {isVideoUrl(previewUrl) || (selectedFile && isVideoFile(selectedFile)) ? (
                            <video src={previewUrl} controls className="max-w-full h-48 rounded-md" />
                          ) : (
                            <img src={previewUrl} alt="Aperçu" className="max-w-full h-48 object-cover rounded-md" />
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end space-x-4">
                    <Button type="button" onClick={resetForm} variant="outline">
                      Annuler
                    </Button>
                    <Button type="submit" className="bg-primary-600 text-white">
                      Ajouter
                    </Button>
                  </div>
                </form>
              </GlassCard>
            </motion.div>
          )}

          {/* Filtres */}
          <div className="mb-8 flex flex-wrap gap-3">
            {categories.map(cat => {
              const Icon = cat.icon
              return (
                <Button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={activeCategory === cat.id ? 'bg-primary-600 text-white' : ''}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {cat.name} ({galleryImages[cat.id]?.length || 0})
                </Button>
              )
            })}
          </div>

          {/* Galerie */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {currentImages.length === 0 ? (
              <div className="col-span-full text-center text-gray-500 py-12">
                Aucune image dans cette catégorie
              </div>
            ) : (
              currentImages.map((image) => (
                <GlassCard key={image.id} className="p-0 overflow-hidden relative group">
                  {image.mediaType === 'video' || (!image.mediaType && isVideoUrl(image.imageUrl)) ? (
                    <video
                      src={image.imageUrl}
                      className="w-full h-48 object-cover"
                      controls
                    />
                  ) : (
                    <img
                      src={image.imageUrl}
                      alt="Gallery"
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x300?text=Image+non+disponible'
                      }}
                    />
                  )}
                  
                  {/* Badge vidéo */}
                  {(image.mediaType === 'video' || (!image.mediaType && isVideoUrl(image.imageUrl))) && (
                    <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center space-x-1">
                      <Video className="w-3 h-3" />
                      <span>MP4</span>
                    </div>
                  )}

                  {/* Bouton supprimer */}
                  <button
                    onClick={() => deleteGalleryImage(activeCategory, image.id)}
                    className="absolute top-2 left-2 bg-red-500 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </GlassCard>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

export default GalleryManagement